import type { ToolActivityOutcome } from "~/shared/agent-core/conversation-contract";

export type ToolActivityState = "active" | "succeeded" | "needs_adjustment" | "blocked" | "failed";

export interface ToolActivityStep {
  id: number;
  name: string;
  label: string;
  doneLabel: string;
  title?: string;
  state: ToolActivityState;
  correctiveCallStarted: boolean;
  callId?: string;
}

export interface ToolActivityEntry {
  id: number;
  name: string;
  label: string;
  doneLabel: string;
  title?: string;
  callId?: string;
}

export const appendToolActivityStep = (steps: ToolActivityStep[], entry: ToolActivityEntry): ToolActivityStep[] => {
  let correctiveIndex = -1;
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step?.name === entry.name && step.state === "needs_adjustment") {
      correctiveIndex = index;
      break;
    }
  }
  const updated = steps.map((step, index) => (index === correctiveIndex ? { ...step, correctiveCallStarted: true } : step));
  return [...updated, { ...entry, state: "active", correctiveCallStarted: false }];
};

export const resolveToolActivityStep = (
  steps: ToolActivityStep[],
  name: string,
  outcome: ToolActivityOutcome,
  callId?: string,
): ToolActivityStep[] => {
  let matchIndex = -1;
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step?.state !== "active") {
      continue;
    }
    if (callId !== undefined ? step.callId === callId : step.name === name) {
      matchIndex = index;
      break;
    }
  }
  if (matchIndex === -1) {
    return steps;
  }
  if (outcome === "reused") {
    return steps.filter((_, index) => index !== matchIndex);
  }
  return steps.map((step, index) => (index === matchIndex ? { ...step, state: outcome } : step));
};

export const failActiveSteps = (steps: ToolActivityStep[]): ToolActivityStep[] =>
  steps.some(step => step.state === "active") ? steps.map(step => (step.state === "active" ? { ...step, state: "failed" } : step)) : steps;

export const hasVisibleActivityTrace = (steps: ToolActivityStep[]): boolean => steps.length > 0;

export const clearActivitySteps = (steps: ToolActivityStep[]): ToolActivityStep[] => (steps.length === 0 ? steps : []);
